import type {
  ColourFinish,
  FlowItemContextValue,
  FlowSelectionContextValue,
  RequestItem,
  SelectedVase,
} from '../types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function isSelectedVase(value: unknown): value is SelectedVase {
  const record = asRecord(value);
  return Boolean(record) && Number.isFinite(record.id) && typeof record.name === 'string';
}

function cloneSnapshot<T>(value: T): T {
  if (value == null) return value;
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

export function buildSelectionContext(args: {
  product: unknown;
  finish: unknown;
  colours: unknown;
  combination: unknown;
}): FlowSelectionContextValue | null {
  const product = isSelectedVase(args.product) ? cloneSnapshot(args.product) : null;
  const colours = Array.isArray(args.colours)
    ? args.colours.filter((entry): entry is string => typeof entry === 'string')
    : [];
  const combination = asRecord(args.combination)
    ? cloneSnapshot(asRecord(args.combination))
    : null;
  const finish =
    typeof args.finish === 'string' && args.finish.trim().length > 0
      ? (args.finish as ColourFinish)
      : null;
  if (!product && !finish && colours.length === 0 && !combination) return null;
  return {
    product,
    productId: product?.id ?? null,
    finish,
    colours,
    combination,
  };
}

export function asSelectionContext(value: unknown): FlowSelectionContextValue | null {
  if (isSelectedVase(value)) {
    return buildSelectionContext({
      product: value,
      finish: null,
      colours: [],
      combination: null,
    });
  }
  const record = asRecord(value);
  if (!record) return null;
  const context = buildSelectionContext({
    product: record.product,
    finish: record.finish,
    colours: record.colours,
    combination: record.combination,
  });
  if (context) return context;
  const productId = Number.isFinite(record.productId) ? Number(record.productId) : null;
  if (productId == null) return null;
  return {
    product: null,
    productId,
    finish: null,
    colours: [],
    combination: null,
  };
}

export function asRequestItem(value: unknown): RequestItem | null {
  const record = asRecord(value);
  if (!record) return null;
  const colours = Array.isArray(record.colours)
    ? record.colours.filter((entry): entry is string => typeof entry === 'string')
    : [];
  return {
    id:
      typeof record.id === 'string' && record.id.trim().length > 0
        ? record.id
        : `request-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    product: isSelectedVase(record.product) ? record.product : null,
    finish:
      typeof record.finish === 'string' && record.finish.trim().length > 0
        ? (record.finish as ColourFinish)
        : null,
    colours,
    combination: asRecord(record.combination) as RequestItem['combination'],
    productName:
      typeof record.productName === 'string' && record.productName.trim().length > 0
        ? record.productName
        : null,
    productPrice: Number.isFinite(record.productPrice) ? Number(record.productPrice) : null,
    productMaterial:
      typeof record.productMaterial === 'string' && record.productMaterial.trim().length > 0
        ? record.productMaterial
        : null,
  };
}

export function asRequestItemList(value: unknown): RequestItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => asRequestItem(entry))
    .filter((entry): entry is RequestItem => Boolean(entry));
}

export function buildRequestItemFromSelection(args: {
  product: unknown;
  finish: unknown;
  colours: unknown;
  combination: unknown;
  productName?: unknown;
  productPrice?: unknown;
  productMaterial?: unknown;
}): RequestItem | null {
  const product = isSelectedVase(args.product) ? args.product : null;
  if (!product) return null;
  const colours = Array.isArray(args.colours)
    ? args.colours.filter((entry): entry is string => typeof entry === 'string')
    : [];
  return {
    id: `request-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    product,
    finish:
      typeof args.finish === 'string' && args.finish.trim().length > 0
        ? (args.finish as ColourFinish)
        : null,
    colours,
    combination: asRecord(args.combination) as RequestItem['combination'],
    productName:
      typeof args.productName === 'string' && args.productName.trim().length > 0
        ? args.productName
        : null,
    productPrice: Number.isFinite(args.productPrice) ? Number(args.productPrice) : null,
    productMaterial:
      typeof args.productMaterial === 'string' && args.productMaterial.trim().length > 0
        ? args.productMaterial
        : null,
  };
}

export function buildRequestItemFromSelectionContext(
  selection: FlowSelectionContextValue | null,
  args?: {
    productName?: unknown;
    productPrice?: unknown;
    productMaterial?: unknown;
  }
): RequestItem | null {
  if (!selection) return null;
  return buildRequestItemFromSelection({
    product: selection.product,
    finish: selection.finish,
    colours: selection.colours,
    combination: selection.combination,
    productName: args?.productName,
    productPrice: args?.productPrice,
    productMaterial: args?.productMaterial,
  });
}

export function buildItemContextFromSelection(
  selection: FlowSelectionContextValue | null,
  args?: {
    productName?: unknown;
    productPrice?: unknown;
    productMaterial?: unknown;
  }
): FlowItemContextValue | null {
  const requestItem = buildRequestItemFromSelectionContext(selection, args);
  if (!requestItem) return null;
  return {
    selection: selection ? cloneSnapshot(selection) : null,
    requestItem: cloneSnapshot(requestItem),
    productId: requestItem.product?.id ?? selection?.productId ?? null,
  };
}

export function asItemContext(value: unknown): FlowItemContextValue | null {
  const record = asRecord(value);
  if (!record) return null;
  const selection = asSelectionContext(record.selection);
  const requestItem = asRequestItem(record.requestItem);
  const productId = Number.isFinite(record.productId)
    ? Number(record.productId)
    : requestItem?.product?.id ?? selection?.productId ?? null;
  if (!selection && !requestItem && productId == null) return null;
  return {
    selection: selection ? cloneSnapshot(selection) : null,
    requestItem: requestItem ? cloneSnapshot(requestItem) : null,
    productId,
  };
}

export function requestItemFromItemContext(value: unknown): RequestItem | null {
  const itemContext = asItemContext(value);
  if (itemContext?.requestItem) return cloneSnapshot(itemContext.requestItem);
  return asRequestItem(value);
}

export function appendRequestItem(listValue: unknown, itemValue: unknown) {
  const list = asRequestItemList(listValue);
  const item = requestItemFromItemContext(itemValue);
  if (!item) return list;
  return [...list, item];
}

function requestItemsMatch(left: RequestItem, right: RequestItem) {
  if (left.id === right.id) return true;
  return (
    left.product?.id === right.product?.id &&
    left.finish === right.finish &&
    JSON.stringify(left.colours) === JSON.stringify(right.colours) &&
    JSON.stringify(left.combination ?? null) === JSON.stringify(right.combination ?? null)
  );
}

export function removeRequestItem(listValue: unknown, itemValue: unknown) {
  const list = asRequestItemList(listValue);
  const item = requestItemFromItemContext(itemValue);
  if (!item) return list;
  let removed = false;
  return list.filter((entry) => {
    if (removed) return true;
    if (!requestItemsMatch(entry, item)) return true;
    removed = true;
    return false;
  });
}

export function clearRequestItemList() {
  return [] as RequestItem[];
}
